import React, { useCallback, useEffect, useState } from "react";
import "./index.less";
import { t } from "i18next";
import applePayIcon from "../../assets/icons/applePay3.svg";
import { message, Row, Typography } from "antd";

declare global {
  interface Window {
    paysafe: any; // Declare 'paysafe' as any type since it is loaded dynamically
  }
}

interface ApplePayButtonProps {
  paymentAmount: any;
  label : string,
  handleApplePayPayment: any;
  isValidated: any;
}

const DecryptApplePay: React.FC<ApplePayButtonProps> = ({ paymentAmount, label, handleApplePayPayment , isValidated}) => {
  const [hasError, setHasError] = useState(false);
  const [instance, setInstance] = useState<any>(null);
  const isAndroidOrWindows = /android|windows/i.test(navigator.userAgent);

  const generateGuid = () => {
    return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
      const r = (crypto.getRandomValues(new Uint8Array(1))[0] & 15) >> 0;
      const v = c === "x" ? r : (r & 0x3) | 0x8;
      return v.toString(16);
    });
  };

  const log = (message: any) => {
    const outputElement = document.getElementById("output");
    if (outputElement) {
      outputElement.innerHTML += `<br>[${new Date().toLocaleTimeString()}] ${JSON.stringify(message)}`;
    }
    // console.log(message);
    // alert(message);
  };

  const tokenize = useCallback(async () => {

    if (!isValidated) {
      return message.error(t('email-invalid'));                            // ⛔️ stop here – do NOT open Apple Pay
    }

    if (!instance) {
      log("Payment instance is not initialized.");
      return;
    }

    const amount = Math.round(parseFloat(paymentAmount.toFixed(2)) * 100);
    log(`Amount to be charged: ${amount}`);

    const merchantRefNum = generateGuid();
    log(`Generated Merchant Reference Number: ${merchantRefNum}`);

    const tokenizationOptions = {
      amount: amount,
      transactionType: "PAYMENT",
      paymentType: "APPLEPAY",
      merchantRefNum,
      applePay: {
        country: "US",
        requestBillingAddress: true,
      },
      customerDetails: {
        profile: {
          firstName: 'Unlock', // These can be dynamic, assuming they are available
          lastName: 'link',
          email: 'unlocklink@gmail.com',
        },
      },
    };

    log("Starting tokenization process...");

    try {
      const result = await instance.tokenize(tokenizationOptions);
      log("Tokenization resolved.");
      log(result);

      const billingContact = result?.applePay?.billingContact || {};
      const billingDetails = {
        street: billingContact.addressLines?.[0] || "",
        city: billingContact.locality || "",
        zip: billingContact.postalCode || "",
        country: billingContact.countryCode || "",
        state: billingContact.administrativeArea || "",
      };

      const paymentPayload = {
        merchantRefNum: tokenizationOptions.merchantRefNum,
        amount: tokenizationOptions.amount,
        currencyCode: "EUR",
        paymentHandleToken: result.token,
        description: "Demo Apple Pay Paysafe Js",
        billingDetails
      };

      await logPost("https://api.test.paysafe.com/paymenthub/v1/payments", paymentPayload);
      instance.complete("success");
      handleApplePayPayment(result.token);
    } catch (error) {
      log("Error on tokenization.");
      log(error);

      // We must close the Apple Pay window
      instance.complete("fail");
    }
  }, [instance, paymentAmount, label , isValidated]);

  const logPost = async (url: string, obj: any) => {
    const request = `curl --location --request POST '${url}' \
--header 'Authorization: Basic <(encode Base64) atlas_testing_private:(password from Geneva) >' \
--header 'Content-Type: application/json' \
--data-raw '${JSON.stringify(obj).replace(/\\"/g, '"')}'`;

    // const outputElement = document.getElementById("output");
    // if (outputElement) {
    //   let output = outputElement.innerHTML;
    //   output += `<br>[${getCurrentTime()}] ${request}`;
    //   outputElement.innerHTML = output;
    // }
  };

  const getCurrentTime = () => {
    const currentDate = new Date();
    return currentDate.toISOString().split("T")[1].split(".")[0]; // Better time format
  };

  const setupPaysafe = () => {
    if (isAndroidOrWindows) return;

    const API_KEY = process.env.REACT_APP_PAY_SAFE_API_KEY;
    const ENVIRONMENT = process.env.REACT_APP_PAY_SAFE_ENVIRONMENT || "TEST";
    const ACCOUNT_ID = process.env.REACT_APP_PAY_SAFE_ACCOUNT_ID;

    log(`Setting up Paysafe with label: ${label}`);

    const options = {
      currencyCode: "EUR",
      environment: ENVIRONMENT,
      accounts: {
        default: Number(ACCOUNT_ID),
      },
      fields: {
        applePay: {
          selector: "#apple-pay",
          type: "buy",
          label: label,
          color: "white-outline",
        },
      },
    };

    let mounted = true;
    window.paysafe?.fields
      .setup(API_KEY, options)
      .then((inst: any) => {
        if (!mounted) return;
        log("Paysafe instance successfully set up.");
        setInstance(inst);

        if (!inst.paymentMethods.applePay?.error) {
          log("Apple Pay is available.");
        } else {
          setHasError(true);
          log("Apple Pay not available.");
        }
      })
      .catch((error: any) => {
        setHasError(true);
        log(`Paysafe setup error: ${error}`);
      });

    return () => {
      mounted = false;
      setInstance(null);
      log("Component unmounted, instance reset.");
    };
  };

  useEffect(setupPaysafe, [isAndroidOrWindows, label]);

  if (hasError) {
    return <div>{t("Payment method not available")}</div>;
  }

  if (isAndroidOrWindows) {
    return null; // Don't show on unsupported devices
  }

  return (
    <div>
      <div
        id="apple-pay"
        onClick={tokenize}
        style={{
          width: "90vw",
          maxWidth: "930px",
          borderRadius: "8px",
          marginTop: "20px",
        }}
      >
        <button className="applePayBtn">
          <img src={applePayIcon} alt="Apple Pay" width="45px" />
        </button>
      </div>
      <Row className="mt-20 mb-20 justify-content-center">
        <Typography.Text className="text-white-color font-18-bold">{t("auth-signUp-divider")}</Typography.Text>
      </Row>
    </div>
  );
};

export default DecryptApplePay;
